import React, { useState, useEffect } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FiPlus, FiX } from "react-icons/fi";
import PageLayout from "../../layouts/PageLayout";

export default function SellerAddProductPage() {
    const navigate = useNavigate();

    const [loadingInit, setLoadingInit] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Initial Data States
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [selectedParentCategory, setSelectedParentCategory] = useState("");

    // Form State aligned with storeFreshProduct requirements
    const [formData, setFormData] = useState({
        category_id: "",
        brand_id: "",
        title: "",
        product_condition: "new",
        buying_price: "",
        sell_price_dollar: "",
        shipping_charges_dollar: "",
        short_buying_price: "",
        buying_price: "",
        product_qty: 1,
        short_description: "",
        description: "",
    });

    const [mainImage, setMainImage] = useState(null);
    const [mainImagePreview, setMainImagePreview] = useState(null);
    const [galleryFiles, setGalleryFiles] = useState([]);
    const [galleryPreviews, setGalleryPreviews] = useState([]);

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem("authToken") || localStorage.getItem("AuthUser")}`,
    });

    // Fetch Initial Brands & Categories
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/store-seller/initial-data`,
                    { headers: getAuthHeaders() }
                );
                setBrands(res.data?.brand || []);
                setCategories(res.data?.product_categories || []);
            } catch (err) {
                console.error("Fetch initial data error:", err);
                setError("Failed to load categories and brands.");
            } finally {
                setLoadingInit(false);
            }
        };

        fetchInitialData();
    }, []);

    // Parent Category Selection Logic
    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);
        setFormData((prev) => ({ ...prev, category_id: "" }));

        const selectedCat = categories.find((c) => String(c.id) === String(parentId));
        if (selectedCat && selectedCat.child_categories) {
            setSubCategories(selectedCat.child_categories);
        } else {
            setSubCategories([]);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleQuillChange = (value) => {
        setFormData((prev) => ({ ...prev, description: value }));
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    };

    const handleGalleryImagesChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setGalleryFiles((prev) => [...prev, ...files]);
        const newPreviews = files.map((file) => URL.createObjectURL(file));
        setGalleryPreviews((prev) => [...prev, ...newPreviews]);
    };

    const removeGalleryImage = (index) => {
        setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
        setGalleryPreviews((prev) => prev.filter((_, i) => i !== index));
    };

    const validateForm = () => {
        if (!formData.title.trim()) {
            setError("Product title is required.");
            return false;
        }
        if (!formData.category_id) {
            setError("Please select a sub category.");
            return false;
        }
        if (!formData.brand_id) {
            setError("Please select a brand.");
            return false;
        }
        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter a valid Buying Price.");
            return false;
        }
        if (formData.sell_price_dollar === "" || Number(formData.sell_price_dollar) < 0) {
            setError("Please enter sell price in dollar.");
            return false;
        }
        if (formData.shipping_charges_dollar === "" || Number(formData.shipping_charges_dollar) < 0) {
            setError("Please enter shipping charges in dollar.");
            return false;
        }
        if (formData.buying_price === "" || Number(formData.buying_price) < 0) {
            setError("Please enter buying price.");
            return false;
        }
        if (formData.short_buying_price === "" || Number(formData.short_buying_price) < 0) {
            setError("Please enter short buying price.");
            return false;
        }
        if (!formData.short_description.trim()) {
            setError("Short description is required.");
            return false;
        }
        if (!formData.description.trim() || formData.description === "<p><br></p>") {
            setError("Full description is required.");
            return false;
        }
        if (!mainImage) {
            setError("Main product card image (card_img) is required.");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!validateForm()) return;
        setSaving(true);

        try {
            const submitData = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== undefined && formData[key] !== "") {
                    submitData.append(key, formData[key]);
                }
            });

            if (mainImage) {
                submitData.append("card_img", mainImage);
            }

            // Call storeFreshProduct Endpoint
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/store-seller/new-products`,
                submitData,
                {
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const createdProduct = response.data?.data || response.data?.product || response.data;
            const productId = createdProduct?.id;

            // Upload Gallery Images if available
            if (productId && galleryFiles.length > 0) {
                for (let file of galleryFiles) {
                    const imgFormData = new FormData();
                    imgFormData.append("model", "products");
                    imgFormData.append("id", productId);
                    imgFormData.append("image", file);

                    await axios.post(
                        `${process.env.REACT_APP_API_URL}/store-seller/upload-image`,
                        imgFormData,
                        {
                            headers: {
                                ...getAuthHeaders(),
                                "Content-Type": "multipart/form-data",
                            },
                        }
                    );
                }
            }

            setSuccess("Fresh product created successfully!");
            setTimeout(() => navigate("/seller-products"), 1200);
        } catch (err) {
            console.error("Create fresh product error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to create product.");
            }
        } finally {
            setSaving(false);
        }
    };

    if (loadingInit) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Loading Categories & Brands...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Add Fresh Product</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to="#">Home</Link></li>
                                <li className="mc-breadcrumb-item">Add Fresh Product</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
                </Col>

                <Col xl={12}>
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Product Info */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Basic Product Information</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                placeholder="e.g. Premium Wireless Headphones"
                                                value={formData.title}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    {/* Parent Category Select */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Main Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                value={selectedParentCategory}
                                                onChange={handleParentCategoryChange}
                                                required
                                            >
                                                <option value="">Select Main Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Child Category Select */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleChange}
                                                disabled={!subCategories.length}
                                                required
                                            >
                                                <option value="">
                                                    {subCategories.length ? "Select Sub Category" : "No Sub Categories Available"}
                                                </option>
                                                {subCategories.map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Brand Select */}
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Brand <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="brand_id"
                                                value={formData.brand_id}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Brand</option>
                                                {brands.map((b) => (
                                                    <option key={b.id} value={b.id}>{b.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    {/* Condition Select */}
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Condition <span className="text-danger">*</span></Form.Label>
                                            <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange} required>
                                                <option value="new">New</option>
                                                <option value="used">Used</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="product_qty"
                                                placeholder="e.g. 50"
                                                value={formData.product_qty}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Pricing & Costs */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Pricing & Cost Details</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="buying_price"
                                                placeholder="e.g. 1500.00"
                                                value={formData.buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sell Price Dollar ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="sell_price_dollar"
                                                placeholder="e.g. 25.50"
                                                value={formData.sell_price_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Shipping Charges ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="shipping_charges_dollar"
                                                placeholder="e.g. 5.00"
                                                value={formData.shipping_charges_dollar}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="buying_price"
                                                placeholder="e.g. 1000"
                                                value={formData.buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                name="short_buying_price"
                                                placeholder="e.g. 950"
                                                value={formData.short_buying_price}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Descriptions */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Overview</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Description <span className="text-danger">*</span></Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="short_description"
                                                placeholder="Provide brief summary of product..."
                                                value={formData.short_description}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col xs={12}>
                                        <Form.Group className="mb-2">
                                            <Form.Label>Full Description <span className="text-danger">*</span></Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                placeholder="Enter full specification and details..."
                                                value={formData.description}
                                                onChange={handleQuillChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Image Uploads */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header>
                                <h5 className="mb-0">Main Card Image <span className="text-danger">*</span></h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="align-items-center">
                                    <Col md={3}>
                                        <div style={{ width: "100%", height: "150px", border: "1px dashed #ccc", borderRadius: "8px", overflow: "hidden" }}>
                                            {mainImagePreview ? (
                                                <img src={mainImagePreview} alt="Main Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                                            ) : (
                                                <div className="d-flex h-100 align-items-center justify-content-center text-muted">No Card Image Selected</div>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={9}>
                                        <Form.Control type="file" name="card_img" accept="image/jpg,image/jpeg,image/png,image/webp" onChange={handleMainImageChange} required />
                                        <Form.Text className="text-muted">Required image extensions: JPG, JPEG, PNG, WEBP (Max: 5MB)</Form.Text>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Gallery Images */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Gallery Images (Optional)</h5></Card.Header>
                            <Card.Body>
                                <Row className="g-3">
                                    {galleryPreviews.map((src, idx) => (
                                        <Col md={3} sm={4} key={idx}>
                                            <div className="position-relative" style={{ height: "140px", border: "1px solid #ddd", borderRadius: "6px", overflow: "hidden" }}>
                                                <img src={src} alt={`Gallery ${idx}`} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    className="position-absolute top-0 end-0 m-1 p-1 rounded-circle"
                                                    onClick={() => removeGalleryImage(idx)}
                                                >
                                                    <FiX size={14} />
                                                </Button>
                                            </div>
                                        </Col>
                                    ))}
                                    <Col md={3} sm={4}>
                                        <label
                                            className="d-flex flex-column align-items-center justify-content-center border border-primary border-dashed rounded text-primary"
                                            style={{ height: "140px", cursor: "pointer", backgroundColor: "#f8f9fa" }}
                                        >
                                            <FiPlus size={28} />
                                            <span className="fw-bold mt-1">Add Images</span>
                                            <input type="file" hidden multiple accept="image/jpg,image/jpeg,image/png,image/webp" onChange={handleGalleryImagesChange} />
                                        </label>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        <div className="d-flex justify-content-end gap-2 mb-5">
                            <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                            <Button type="submit" variant="primary" disabled={saving}>
                                {saving ? <Spinner size="sm" animation="border" /> : "Save Fresh Product"}
                            </Button>
                        </div>
                    </Form>
                </Col>
            </Row>
        </PageLayout>
    );
}